import React, { useEffect, useState } from 'react';
import { BaseDialog } from '../../base/dialog';
import { JsonDiffReact } from '../../json-diff';
import { CoreFetch } from '../../../api';
import { CodingApi } from '../../../api/server';
import { ProviderCoding, SupportCoding } from '../../../api/server/models';
import styled from '@emotion/styled';
import { Variables } from '../../base/variables';

interface ICodingDetailModalProps {
    codingId: string;
    isSupportApp: boolean;
    open: boolean;
    onClose: () => void;
}

// Styled components similar to claim-timeline-table styles
const ExpandWrap = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
`;

const ExpandCol = styled.div`
    width: 100%;
`;

const ExpandTable = styled.table`
    white-space: normal;
    color: ${Variables.Colors.n100};
    margin: 0;
    width: 100%;
`;

const ExpandTD = styled.td<{ colSpan?: number }>`
    text-align: left;
    font-size: 16px;
    letter-spacing: 0.5px;
    padding: 4px 8px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ExpandTH = styled.th`
    text-align: left;
    vertical-align: top;
    font-weight: 500;
    font-size: 16px;
    letter-spacing: 0.5px;
    padding: 4px 8px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ExpandTR = styled.tr``;
const ExpandTBody = styled.tbody``;

const LoadingMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: ${Variables.Colors.n70};
`;

const ErrorMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: ${Variables.Colors.r50};
`;

export const CodingDetailModal: React.FunctionComponent<ICodingDetailModalProps> = (
    props: ICodingDetailModalProps
): React.ReactElement => {
    const { codingId, isSupportApp, open, onClose } = props;
    const [coding, setCoding] = useState<ProviderCoding | SupportCoding | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && codingId) {
            fetchCodingDetails();
        }
    }, [open, codingId, isSupportApp]);

    const fetchCodingDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const codingApi = CoreFetch(CodingApi);
            let codingData: ProviderCoding | SupportCoding;
            
            if (isSupportApp) {
                codingData = await codingApi.getSupportCoding({ id: codingId });
            } else {
                codingData = await codingApi.getProviderCoding({ id: codingId });
            }
            
            setCoding(codingData);
        } catch (err) {
            console.error('Failed to fetch coding details:', err);
            setError('Failed to load coding details');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (loading) {
            return <LoadingMessage>Loading coding details...</LoadingMessage>;
        }

        if (error) {
            return <ErrorMessage>{error}</ErrorMessage>;
        }

        if (!coding) {
            return <LoadingMessage>No coding data available</LoadingMessage>;
        }

        let codingNotes = coding.notes;
        if (codingNotes && typeof codingNotes === 'string') {
            try {
                const parsed = JSON.parse(codingNotes);
                codingNotes = parsed;
            } catch (parseError) {
                console.error('Failed to parse coding notes as JSON:', parseError, codingNotes);
                codingNotes = 'Invalid JSON';
            }
        }

        return (
            <ExpandWrap key={coding.id}>
                <ExpandCol>
                    <ExpandTable>
                        <ExpandTBody>
                            <ExpandTR>
                                <ExpandTH>Coding ID</ExpandTH>
                                <ExpandTD>{coding.id}</ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTH>Vendor</ExpandTH>
                                <ExpandTD>{coding.vendor}</ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTH>Status</ExpandTH>
                                <ExpandTD>{coding.status}</ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTH>Notes</ExpandTH>
                                <ExpandTD>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {codingNotes ? (typeof codingNotes === 'string' ? codingNotes : JSON.stringify(codingNotes, null, 2)) : '-'}
                                    </pre>
                                </ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTD colSpan={2}>
                                    <table style={{ width: '100%' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ width: '50%' }}>Before</td>
                                                <td style={{ width: '50%' }}>After</td>
                                            </tr>
                                            <tr>
                                                <td style={{ verticalAlign: 'top', fontSize: '12px', fontFamily: "'Bitstream Vera Sans Mono', 'DejaVu Sans Mono', Monaco, Courier, monospace", width: '50%' }}>
                                                    <pre>{coding.before ? JSON.stringify(coding.before, null, 4) : ''}</pre>
                                                </td>
                                                <td style={{ verticalAlign: 'top', fontSize: '12px', fontFamily: "'Bitstream Vera Sans Mono', 'DejaVu Sans Mono', Monaco, Courier, monospace", width: '50%' }}>
                                                    <pre>{coding.after ? JSON.stringify(coding.after, null, 4) : ''}</pre>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan={2}>Diff</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={2} style={{ verticalAlign: 'top' }}>
                                                    <JsonDiffReact
                                                        right={coding.after}
                                                        left={coding.before}
                                                        show={false}
                                                        annotated={false}
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </ExpandTD>
                            </ExpandTR>
                        </ExpandTBody>
                    </ExpandTable>
                </ExpandCol>
            </ExpandWrap>
        );
    };

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            renderTitle="Coding Details"
            showCloseButton={true}
            maxWidth="lg"
            fullWidth={true}
            expandable={true}
            style={{ zIndex: 10001 }}
            slotProps={{
                backdrop: {
                    style: { zIndex: 10001 }
                },
                paper: {
                    style: { zIndex: 10002 }
                }
            }}
        >
            {renderContent()}
        </BaseDialog>
    );
};

